import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { EMPTY, expand, map, Observable, reduce } from 'rxjs';

import { CreateEntityWidgetConfig } from '../config/create-entity-widget-config.types';
import { jemmaRequestContext, jemmaSecureBaseUrl } from '../http/jemma-request.util';
import {
    JemmaCountry,
    JemmaOrganizationCreateRequest,
    JemmaOrganizationCreateResponseData,
    JemmaOrganizationType,
    JemmaPaged,
    JemmaResponseModel
} from './jemma-organization.types';

const _PAGE_SIZE = 100;

/**
 * Minimal Jemma API client for the widget's Organization tab. `getCountries()`
 * is also reused by the Facility tab (see `JemmaFacilityApiService`) since
 * countries are a shared codeset across both entities.
 *
 * `getOrganizationTypes()` fetches a single page of up to 100 results — that
 * codeset comfortably fits within one page. `getCountries()` cannot make that
 * assumption (~195+ countries), so it walks every page via `expand` and
 * accumulates the results, mirroring the dashboard's `recursiveFetch` utility
 * (`libs/shared/src/utils/recursive-fetch.util.ts`) without taking a
 * dependency on that shared lib — this widget intentionally has no
 * dependency on the host app's workspace libraries (see `jemma-organization.types.ts`).
 *
 * Stateless: every method takes the calling widget instance's config rather than injecting
 * it, so one service instance safely serves several `<wcg-create-entity>` elements with
 * different tokens at once.
 */
@Injectable({ providedIn: 'root' })
export class JemmaOrganizationApiService {
    private readonly _http = inject(HttpClient);

    getOrganizationTypes(config: CreateEntityWidgetConfig): Observable<JemmaOrganizationType[]> {
        const _params = new HttpParams().set('page', 1).set('size', _PAGE_SIZE);
        return this._http
            .get<JemmaResponseModel<JemmaPaged<JemmaOrganizationType>>>(`${jemmaSecureBaseUrl(config)}/organization-types`, {
                params: _params,
                context: jemmaRequestContext(config)
            })
            .pipe(map(response => response.data?.results ?? []));
    }

    getCountries(config: CreateEntityWidgetConfig): Observable<JemmaCountry[]> {
        const _fetchPage = (page: number) =>
            this._http.get<JemmaResponseModel<JemmaPaged<JemmaCountry>>>(`${jemmaSecureBaseUrl(config)}/countries`, {
                params: new HttpParams().set('page', page).set('size', _PAGE_SIZE),
                context: jemmaRequestContext(config)
            });

        return _fetchPage(1).pipe(
            expand(response => {
                const _page = response.data?.page ?? 1;
                const _size = response.data?.size ?? _PAGE_SIZE;
                const _total = response.data?.total ?? 0;
                const _hasMorePages = _page * _size < _total;
                return _hasMorePages ? _fetchPage(_page + 1) : EMPTY;
            }),
            reduce<JemmaResponseModel<JemmaPaged<JemmaCountry>>, JemmaCountry[]>(
                (accumulated, response) => [...accumulated, ...(response.data?.results ?? [])],
                []
            )
        );
    }

    createOrganization(config: CreateEntityWidgetConfig, body: JemmaOrganizationCreateRequest): Observable<JemmaOrganizationCreateResponseData> {
        return this._http
            .post<JemmaResponseModel<JemmaOrganizationCreateResponseData>>(`${jemmaSecureBaseUrl(config)}/organizations`, body, { context: jemmaRequestContext(config) })
            .pipe(
                map(response => {
                    if (!response.data) {
                        throw new Error('Jemma API returned no organization data.');
                    }
                    return response.data;
                })
            );
    }
}
